import { Gift } from "lucide-react";
import { Card } from "@components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@components/ui/table";
import { useState } from "react";

export function PurchaseReferral({
  data,
  loading,
  error,
  startDate,
  endDate,
}: {
  data: any[];
  loading: boolean;
  error: string | null;
  startDate?: Date;
  endDate?: Date;
}) {
  const [sortKey, setSortKey] = useState<string>("purchased");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  let rows = Array.isArray(data) ? data : [];
  if (startDate || endDate) {
    rows = rows.filter((row: any) => {
      const dateStr = row.date;
      if (!dateStr) return true;
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return true;
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    });
  }
  const columns = [
    { key: "playerId", label: "Player ID" },
    { key: "purchased", label: "Purchased" },
    { key: "referral", label: "Referral" },
    { key: "used", label: "Used" },
  ];
  const pageSize = 10;
  const totalPages = Math.ceil(rows.length / pageSize);
  function getValue(row: any, key: string) {
    if (key === "playerId") return row.playerId ?? row.player_id ?? "-";
    if (key === "purchased") return row.purchased ?? row.Purchased ?? "-";
    if (key === "referral") return row.referral ?? row.Referral ?? "-";
    if (key === "used") return row.used ?? row.Used ?? "-";
    return row[key] ?? "-";
  }
  const sortedRows = [...rows].sort((a, b) => {
    const aVal = getValue(a, sortKey);
    const bVal = getValue(b, sortKey);
    if (aVal === bVal) return 0;
    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    }
    if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) {
      return sortDir === "asc"
        ? Number(aVal) - Number(bVal)
        : Number(bVal) - Number(aVal);
    }
    return sortDir === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });
  const pagedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);
  function handleSort(key: string) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  }
  return (
    <Card className="hover:shadow-lg transition-shadow duration-200 overflow-hidden p-6 border border-gray-800">
      <h2 className="text-lg font-semibold font-heading mb-4 flex items-center text-superone-charcoal">
        <Gift className="mr-2 h-5 w-5 text-pink-500" />
        Purchase & Referral
      </h2>
      {loading ? (
        <div className="text-gray-400">Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="relative">
            <Table className="rounded-md overflow-hidden border border-gray-200">
              <TableHeader>
                <TableRow className="bg-[#0F87F2]">
                  {columns.map((col, idx) => (
                    <TableHead
                      key={col.key}
                      style={{ width: col.key === "playerId" ? "60%" : "10%" }}
                      className="select-none text-white text-base cursor-pointer bg-[#0F87F2]"
                      onClick={() => handleSort(col.key)}
                    >
                      <span className="flex items-center gap-1">
                        {col.label}
                        {sortKey === col.key && (
                          <span className="ml-1">
                            {sortDir === "asc" ? (
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-label="sorted ascending"
                              >
                                <path
                                  d="M6 3L10 8H2L6 3Z"
                                  fill="currentColor"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-label="sorted descending"
                              >
                                <path
                                  d="M6 9L2 4H10L6 9Z"
                                  fill="currentColor"
                                />
                              </svg>
                            )}
                          </span>
                        )}
                      </span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedRows.map((p, i) => (
                  <TableRow key={`${p.playerId ?? p.player_id ?? "row"}-${i}`}>
                    {columns.map((col, idx) => (
                      <TableCell
                        key={col.key}
                        style={{
                          width: col.key === "playerId" ? "60%" : "10%",
                        }}
                      >
                        {col.key === "playerId"
                          ? getValue(p, col.key)
                          : getValue(p, col.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="h-1 bg-[#0F87F2] w-full z-10" />
          </div>
          <div className="flex items-center justify-between mt-4">
            <button
              className={`px-3 py-1 rounded text-sm font-medium ${
                page === 1
                  ? "bg-gray-100 text-gray-400 cursor-default"
                  : "bg-[#0F87F2] text-white hover:bg-[#0C6DC2] cursor-pointer"
              }`}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              className={`px-3 py-1 rounded text-sm font-medium ${
                page === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-default"
                  : "bg-[#0F87F2] text-white hover:bg-[#0C6DC2] cursor-pointer"
              }`}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
